import injector from 'vue-inject'
import * as types from './mutation-types'
import shortid from 'shortid'
import * as R from 'ramda'
import * as helpers from './helpers'
import { storeClient } from '@/service/apiClients'

const elementSingularTypes = ['category', 'group', 'item', 'question', 'option', 'question', 'option']
const elementPluralTypes = ['categories', 'groups', 'items', 'questions', 'options', 'questions', 'options']

export default {
  setCurrentElementPath ({ commit }, path) {
    commit(types.SET_CURRENT_ELEMENT_PATH, path)
  },
  // Category
  createCategory ({ commit }) {
    const categoryId = shortid.generate()
    const groupId = shortid.generate()
    commit(types.CREATE_CATEGORY, categoryId)
    commit(types.CREATE_GROUP, { categoryId, groupId })
  },
  setCategoryProperties ({ commit }, payload) { commit(types.SET_CATEGORY_PROPERTIES, payload) },
  updateCategories ({ commit }, payload) { commit(types.UPDATE_CATEGORIES, payload) },
  deleteCategory ({ commit }, categoryId) { commit(types.DELETE_CATEGORY, categoryId) },

  // Group
  createGroup ({ commit }, categoryId) { commit(types.CREATE_GROUP, { categoryId, groupId: shortid.generate() }) },
  setGroupProperties ({ commit }, { groupId, properties }) { commit(types.SET_GROUP_PROPERTIES, { groupId, properties }) },
  deleteGroup ({ commit }, { categoryId, groupId }) { commit(types.DELETE_GROUP, { categoryId, groupId }) },

  // Item
  createItem ({ commit }, groupId) { commit(types.CREATE_ITEM, { id: shortid.generate(), groupId }) },
  setItemProperties ({ state, commit, dispatch }, { itemId, properties, elementLevel, updateChainOption = {}, elementPath = state.currentElementPath }) {
    const id = shortid.generate()
    const index = elementPath[2].index

    commit(types.SET_ITEM_PROPERTIES, { itemId, properties: Object.assign(properties, { id }) })
    let newElementPath = R.update(2, { id, index }, elementPath)
    if (elementLevel !== undefined && elementLevel === updateChainOption.copyLevel && itemId !== state.currentElementPath[elementLevel].id) {
      newElementPath = R.update(elementLevel, { id: state.currentElementPath[elementLevel].id, index: state.currentElementPath[elementLevel].index }, elementPath)
    }
    if (R.equals(elementPath, state.currentElementPath) || (elementLevel < updateChainOption.copyLevel && itemId === state.currentElementPath[elementLevel].id)) {
      commit(types.SET_CURRENT_ELEMENT_PATH, newElementPath)
    }

    const groupId = elementPath[1].id
    dispatch('setGroupProperties', {
      groupId,
      properties: { items: R.update(index, id, state.currentDraft.groups[groupId].items) },
      elementPath: newElementPath
    })
  },
  deleteItem ({ state, commit, dispatch }, { groupId, itemId, index }) {
    dispatch('setGroupProperties', {
      groupId,
      properties: { items: R.remove(index, 1, state.currentDraft.groups[groupId].items) }
    })
  },
  // question
  createQuestion ({ state, commit, dispatch }, { parentType, parentId, elementLevel, title = '新選項', type = injector.get('questionType').radioQuestion, alwaysDisplay = false }) {
    const id = shortid.generate()
    if (type === injector.get('questionType').switchQuestion || type === injector.get('questionType').numberQuestion) {
      const optionId = shortid.generate()
      commit(types.CREATE_OPTION, { id: optionId, title: '', price: 0 })
      commit(types.CREATE_QUESTION, { parentType, parentId, id, title, type, alwaysDisplay, options: [optionId] })
    } else {
      commit(types.CREATE_QUESTION, { parentType, parentId, id, title, type, alwaysDisplay })
    }
    const payload = {
      elementLevel: elementLevel - 1,
      properties: { questions: state.currentDraft[parentType][parentId].questions.concat([id]) }
    }
    payload[`${elementSingularTypes[elementLevel - 1]}Id`] = parentId
    const dispatchType = elementSingularTypes[elementLevel - 1]
    dispatch(`set${dispatchType.charAt(0).toUpperCase() + dispatchType.slice(1)}Properties`, payload)
  },
  setQuestionProperties ({ state, commit, dispatch }, { questionId, elementLevel, properties, updateChainOption = {}, elementPath = state.currentElementPath }) {
    const { type } = properties
    if (type === injector.get('questionType').switchQuestion || type === injector.get('questionType').numberQuestion) {
      const optionId = shortid.generate()
      commit(types.CREATE_OPTION, { id: optionId, title: '', price: 0 })
      properties.options = [optionId]
      properties.defaultValue = undefined
    }
    const id = shortid.generate()
    const index = elementPath[elementLevel].index
    commit(types.SET_QUESTION_PROPERTIES, { questionId, index, properties: Object.assign(properties, { id }) })
    let newElementPath = R.update(elementLevel, { id, index }, elementPath)
    if (elementLevel !== undefined && elementLevel === updateChainOption.copyLevel && questionId !== state.currentElementPath[elementLevel].id) {
      newElementPath = R.update(elementLevel, { id: state.currentElementPath[elementLevel].id, index: state.currentElementPath[elementLevel].index }, elementPath)
    }
    if (R.equals(elementPath, state.currentElementPath) || (elementLevel < updateChainOption.copyLevel && questionId === state.currentElementPath[elementLevel].id)) {
      commit(types.SET_CURRENT_ELEMENT_PATH, newElementPath)
    }

    const parentType = elementPluralTypes[elementLevel - 1]
    const parentId = newElementPath[elementLevel - 1].id
    const payload = {
      elementLevel: elementLevel - 1,
      properties: { questions: R.update(index, id, state.currentDraft[parentType][parentId].questions) },
      elementPath: newElementPath,
      updateChainOption
    }
    payload[`${elementSingularTypes[elementLevel - 1]}Id`] = parentId
    const dispatchType = elementSingularTypes[elementLevel - 1]
    dispatch(`set${dispatchType.charAt(0).toUpperCase() + dispatchType.slice(1)}Properties`, payload)
  },
  deleteQuestion ({ state, commit, dispatch }, { questionId, index, elementLevel }) {
    const parentType = elementPluralTypes[elementLevel - 1]
    const parentId = state.currentElementPath[elementLevel - 1].id
    const payload = {
      elementLevel: elementLevel - 1,
      properties: { questions: R.remove(index, 1, state.currentDraft[parentType][parentId].questions) }
    }
    payload[`${elementSingularTypes[elementLevel - 2]}Id`] = state.currentElementPath[elementLevel - 2].id
    payload[`${elementSingularTypes[elementLevel - 1]}Id`] = parentId
    const dispatchType = elementSingularTypes[elementLevel - 1]

    commit(types.SET_CURRENT_ELEMENT_PATH, state.currentElementPath.slice(0, state.currentElementPath.length - 1))
    dispatch(`set${dispatchType.charAt(0).toUpperCase() + dispatchType.slice(1)}Properties`, payload)
  },

  // option
  createOption ({ state, commit, dispatch }, { questionId, title, price, elementLevel }) {
    const id = shortid.generate()
    commit(types.CREATE_OPTION, { id, title, price })

    dispatch('setQuestionProperties', {
      questionId,
      elementLevel: elementLevel,
      properties: { options: state.currentDraft.questions[questionId].options.concat([id]) }
    })
  },
  setOptionProperties ({ state, commit, dispatch }, { optionId, elementLevel, properties, updateChainOption = {}, elementPath = state.currentElementPath }) {
    const id = shortid.generate()
    const questionId = elementPath[elementLevel - 1].id
    const question = state.currentDraft.questions[questionId]
    const index = elementPath[elementLevel] ? elementPath[elementLevel].index : 0 // 這是給 switch 編輯用的
    commit(types.SET_OPTION_PROPERTIES, { questionId, optionId, index, properties: Object.assign(properties, { id }) })
    let newElementPath = R.update(elementLevel, { id, index }, elementPath)
    if (elementLevel !== undefined && elementLevel === updateChainOption.copyLevel && optionId !== state.currentElementPath[elementLevel].id) {
      newElementPath = R.update(elementLevel, { id: state.currentElementPath[elementLevel].id, index: state.currentElementPath[elementLevel].index }, elementPath)
    }
    if (R.equals(elementPath, state.currentElementPath) || (elementLevel < updateChainOption.copyLevel && optionId === state.currentElementPath[elementLevel].id)) {
      commit(types.SET_CURRENT_ELEMENT_PATH, newElementPath)
    }

    const props = { options: R.update(index, id, question.options) }
    // TODO: checkbox 也要修改這裡
    if (question.defaultValue === question.options[index]) {
      props.defaultValue = id
    }
    dispatch('setQuestionProperties', { questionId, elementLevel: elementLevel - 1, properties: props, updateChainOption, elementPath: newElementPath })
  },
  deleteOption ({ state, commit, dispatch }, { questionId, optionId, index, elementLevel }) {
    const properties = { options: R.remove(index, 1, state.currentDraft.questions[questionId].options) }
    const question = state.currentDraft.questions[questionId]
    if (question.defaultValue === question.options[index]) {
      properties.defaultValue = null
    }
    commit(types.SET_CURRENT_ELEMENT_PATH, state.currentElementPath.slice(0, state.currentElementPath.length - 1))
    dispatch('setQuestionProperties', { questionId, elementLevel: elementLevel - 1, properties })
  },

  // Draft
  setCurrentDraft ({ commit }, draft) {
    commit(types.SET_CURRENT_DRAFT, draft)
  },
  setCurrentDraftInfo ({ commit }, { variation, info }) {
    commit(types.SET_DRAFT_INFO, { variation, info })
  },
  async saveDraft ({ state, rootGetters, commit }, variation) {
    try {
      if (state.modificationCount === 0 || state.updateCurrentDraftState.type === injector.get('apiStateType').sent) {
        return
      }

      if (!rootGetters.storeId) {
        throw new Error('No storeId.')
      }

      const currentDraftPromise = storeClient.put(`/stores/${rootGetters.storeId}/menus/drafts/${variation}`, helpers.cleanDraft(state.currentDraft).draft)
      commit(types.SET_UPDATE_CURRENT_DRAFT_STATE, { type: injector.get('apiStateType').sent })
      await currentDraftPromise
      commit(types.SET_UPDATE_CURRENT_DRAFT_STATE, { type: injector.get('apiStateType').ok })
      commit(types.RESET_MODIFICATION_COUNT)
    } catch (e) {
      commit(types.SET_UPDATE_CURRENT_DRAFT_STATE, { type: injector.get('apiStateType').error, payload: { msg: e.message } })
    }
  },
  setUpdateCurrentDraftState ({ commit }, state) {
    if (typeof state === 'string') {
      commit(types.SET_UPDATE_CURRENT_DRAFT_STATE, { type: state })
    } else if (typeof state === 'object') {
      commit(types.SET_UPDATE_CURRENT_DRAFT_STATE, state)
    }
  },
  async getMenuDraft ({ rootGetters, commit }, variation) {
    try {
      if (!rootGetters.storeId) {
        throw new Error('No storeId.')
      }
      const { data: currentDraft } = await storeClient.get(`/stores/${rootGetters.storeId}/menus/drafts/${variation}`)

      commit(types.SET_GET_CURRENT_DRAFT_STATE, { type: injector.get('apiStateType').sent })
      commit(types.SET_CURRENT_DRAFT, currentDraft)
      commit(types.SET_GET_CURRENT_DRAFT_STATE, { type: injector.get('apiStateType').ok })
    } catch (e) {
      commit(types.SET_GET_CURRENT_DRAFT_STATE, { type: injector.get('apiStateType').error })
    }
  },
  setGetCurrentDraftState ({ commit }, state) {
    if (typeof state === 'string') {
      commit(types.SET_GET_CURRENT_DRAFT_STATE, { type: state })
    } else if (typeof state === 'object') {
      commit(types.SET_GET_CURRENT_DRAFT_STATE, state)
    }
  },
  async getMenuDraftList ({ rootGetters, commit }) {
    if (rootGetters.storeId) {
      try {
        const { data: menuDraftList } = await storeClient.get(`/stores/${rootGetters.storeId}/menus/draftList`)
        commit(types.SET_MENU_DRAFT_LIST, menuDraftList)
      } catch (e) {
        console.error('getMnueDraftList failed.')
      }
    }
  },
  setCreateDraftState ({ commit }, state) {
    if (typeof state === 'string') {
      commit(types.SET_CREATE_DRAFT_STATE, { type: state })
    } else if (typeof state === 'object') {
      commit(types.SET_CREATE_DRAFT_STATE, state)
    }
  },
  async createDraft ({ state, rootGetters, commit }) {
    try {
      const newDraftPromise = storeClient.post(`/stores/${rootGetters.storeId}/menus/draft`, {
        variation: shortid.generate(),
        title: '新菜單'
      })
      commit(types.SET_CREATE_DRAFT_STATE, { type: injector.get('apiStateType').sent })
      const { data: newDraft } = await newDraftPromise
      commit(types.SET_MENU_DRAFT_LIST, [...state.menuDraftList, newDraft])
      commit(types.SET_CREATE_DRAFT_STATE, { type: injector.get('apiStateType').ok })
    } catch (e) {
      commit(types.SET_CREATE_DRAFT_STATE, {
        type: injector.get('apiStateType').error,
        payload: {}
      })
    }
  },
  setDeleteDraftState ({ commit }, state) {
    if (typeof state === 'string') {
      commit(types.SET_DELETE_DRAFT_STATE, { type: state })
    } else if (typeof state === 'object') {
      commit(types.SET_DELETE_DRAFT_STATE, state)
    }
  },
  async deleteDraft ({ state, rootGetters, commit }, variation) {
    try {
      const deletePromise = storeClient.delete(`/stores/${rootGetters.storeId}/menus/drafts/${variation}`)
      commit(types.SET_DELETE_DRAFT_STATE, { type: injector.get('apiStateType').sent })
      await deletePromise
      commit(types.SET_MENU_DRAFT_LIST, state.menuDraftList.filter(m => m.variation !== variation))
      commit(types.SET_DELETE_DRAFT_STATE, { type: injector.get('apiStateType').ok })
    } catch (e) {
      commit(types.SET_DELETE_DRAFT_STATE, {
        type: injector.get('apiStateType').error,
        payload: {}
      })
    }
  },
  cleanDraft ({ state, commit }) {
    try {
      const { title, variation } = state.currentDraft
      const { draft, identicalElementTable } = helpers.cleanDraft(state.currentDraft)
      commit(types.SET_CURRENT_ELEMENT_PATH, state.currentElementPath.map(({ id, index }) => ({ id: identicalElementTable[id] || id, index })))
      commit(types.SET_CURRENT_DRAFT, Object.assign(draft, { title, variation }))
    } catch (e) {
      console.log('action.cleanDraft: ' + e.message)
    }
  }
}
